export default function CanvasSettingsModal({
  pendingCols,
  pendingRows,
  pendingDesignMode,
  pendingAida,
  pendingMargin,
  pendingGridThin,
  pendingGridColor,
  pendingFabricColor,
  pendingAllowFractionalStitches,
  fractionalDisabled = false,
  setPendingCols,
  setPendingRows,
  setPendingDesignMode,
  setPendingAida,
  setPendingMargin,
  setPendingGridThin,
  setPendingGridColor,
  setPendingFabricColor,
  onToggleFractionalStitches = () => {},
  onApply,
  onCancel,
  designModeDisabled = false,
}) {
  const isPixelMode = pendingDesignMode === "pixel";
  const safeAida = Math.max(1, parseFloat(pendingAida) || 1);
  const designWidthInches =
    safeAida > 0 ? (parseInt(pendingCols, 10) || 0) / safeAida : 0;
  const designHeightInches =
    safeAida > 0 ? (parseInt(pendingRows, 10) || 0) / safeAida : 0;

  const handleDesignWidthChange = (event) => {
    const nextWidth = parseFloat(event.target.value);
    if (Number.isNaN(nextWidth)) {
      setPendingCols("");
      return;
    }
    const stitches = Math.max(1, Math.round(nextWidth * safeAida));
    setPendingCols(stitches.toString());
  };

  const handleDesignHeightChange = (event) => {
    const nextHeight = parseFloat(event.target.value);
    if (Number.isNaN(nextHeight)) {
      setPendingRows("");
      return;
    }
    const stitches = Math.max(1, Math.round(nextHeight * safeAida));
    setPendingRows(stitches.toString());
  };

  return (
    <div className="modal-backdrop">
      <div className="modal settings-modal">
        <div className="modal-header">
          <h2>Design Settings</h2>
          <button className="icon-btn" onClick={onCancel}>
            Close
          </button>
        </div>
        <div className="modal-body">
          <div className="setting-group">
            <h3>Design Mode</h3>
            <label>
              Mode:
              <select
                className="select"
                value={pendingDesignMode}
                onChange={(event) => setPendingDesignMode(event.target.value)}
                disabled={designModeDisabled}
              >
                <option value="aida">Cross-stitch (Aida)</option>
                <option value="pixel">Pixel art (OBJ)</option>
              </select>
            </label>
            {designModeDisabled && (
              <p className="hint danger">
                Pro Feature: Upgrade to enable Pixel Art mode.
              </p>
            )}
            {isPixelMode && (
              <p className="hint">
                Pixel art mode hides Aida measurements and fabric sizing.
              </p>
            )}
          </div>

          <hr />

          <div className="setting-group">
            <h3>Grid Size</h3>
            <div className="grid2">
              <label>
                Columns:
                <input
                  type="number"
                  value={pendingCols}
                  onChange={(event) => setPendingCols(event.target.value)}
                  min="1"
                  className="input"
                />
              </label>
              <label>
                Rows:
                <input
                  type="number"
                  value={pendingRows}
                  onChange={(event) => setPendingRows(event.target.value)}
                  min="1"
                  className="input"
                />
              </label>
            </div>
            <p className="hint warning">
              Changing this will clear cells outside the new boundaries.
            </p>
          </div>

          <hr />

          {!isPixelMode && (
            <>
              <div className="setting-group">
                <h3>Design Size (inches)</h3>
                <div className="grid2">
                  <label>
                    Width:
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={
                        Number.isFinite(designWidthInches)
                          ? designWidthInches.toFixed(2)
                          : ""
                      }
                      onChange={handleDesignWidthChange}
                      className="input"
                    />
                  </label>
                  <label>
                    Height:
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={
                        Number.isFinite(designHeightInches)
                          ? designHeightInches.toFixed(2)
                          : ""
                      }
                      onChange={handleDesignHeightChange}
                      className="input"
                    />
                  </label>
                </div>
                <p className="hint">
                  These values are derived from stitches ÷ Aida count. Update
                  them to automatically recalculate the stitch grid for your
                  fabric.
                </p>
              </div>

              <hr />

              <div className="setting-group">
                <h3>Fabric & Margin</h3>
                <div className="grid2">
                  <label>
                    Aida Count:
                    <input
                      type="number"
                      value={pendingAida}
                      onChange={(event) => setPendingAida(event.target.value)}
                      min="1"
                      className="input"
                    />
                  </label>
                  <label>
                    Margin (inches):
                    <input
                      type="number"
                      value={pendingMargin}
                      onChange={(event) => setPendingMargin(event.target.value)}
                      min="0"
                      step="0.5"
                      className="input"
                    />
                  </label>
                </div>
              </div>

              <hr />
            </>
          )}

          <div className="setting-group">
            <h3>Grid Visuals</h3>
            <label
              style={{ display: "flex", flexDirection: "column", marginBottom: 10 }}
            >
              Grid Line Thickness:
              <input
                type="number"
                value={pendingGridThin}
                onChange={(event) => setPendingGridThin(event.target.value)}
                min="1"
                max="3"
                step="0.5"
                className="input"
              />
            </label>

            <div className="grid2">
              <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
                Grid Color:
                <input
                  type="color"
                  value={pendingGridColor}
                  onChange={(event) => setPendingGridColor(event.target.value)}
                  className="color-input-swatch"
                  title="Change the color of the grid lines"
                />
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
                Fabric Color:
                <input
                  type="color"
                  value={pendingFabricColor}
                  onChange={(event) => setPendingFabricColor(event.target.value)}
                  className="color-input-swatch"
                  title="Change the background/fabric color"
                />
              </label>
            </div>
          </div>

          <hr />

          <div className="setting-group">
            <h3>Fractional Stitch Options (Beta)</h3>
            <label
              className="checkbox"
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <input
                type="checkbox"
                checked={pendingAllowFractionalStitches}
                onChange={(event) =>
                  onToggleFractionalStitches(event.target.checked)
                }
                disabled={fractionalDisabled}
              />
              Allow quarter stitches
            </label>
            {fractionalDisabled && (
              <p className="hint danger">
                Pro Feature: Sign in and upgrade to unlock fractional stitches.
              </p>
            )}
            <p className="hint">
              Fractional stitches let you paint quarter blocks via the toolbar's
              “¼ Fractional” button. This feature is still experimental—undo/redo
              and exports are supported, but specialty stitches are coming later.
            </p>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-primary" onClick={onApply}>
            Apply &amp; Resize
          </button>
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
